import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PaginationMetaModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { resetAll } from '../../../../shared/infra/redux/baseActions';
import { ReduxStatus } from '../../../../shared/types';
import { BasicGroupsModel } from '../../members/models/BasicGroups.model';
import { GroupModel } from '../../members/models/Groups.model';
import { MemberModel } from '../../members/models/Member.model';
import {
  createCommunityGroup,
  getBasicCommunityGroups,
  getCommunityGroups,
  getGroupDetails,
  getGroupMembers,
  getCreatedGroupDetails,
  removeGroupMembers,
} from './operators';

export interface GroupsState {
  groups: GroupModel[];
  groupsPagination: PaginationMetaModel;
  basicGroups?: BasicGroupsModel[];
  selectedId: string | null;
  group: GroupModel | null;
  groupMembers: MemberModel[];
  groupMembersPagination: PaginationMetaModel;
  groupFeed: any[];
  groupFeedPagination: PaginationMetaModel;
  createCommunityGroupStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getCommunityGroupsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getBasicCommunityGroupStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getGroupDetailsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getGroupMembersStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getGroupFeedStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  getCreatedGroupDetailsStatus: {
    status: ReduxStatus;
    error: string | null;
  };
  removeGroupMembersStatus: {
    status: ReduxStatus;
    error: string | null;
  };
}

const initialState: GroupsState = {
  groups: [],
  groupsPagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  basicGroups: undefined,
  selectedId: null,
  group: null,
  groupMembers: [],
  groupMembersPagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  groupFeed: [],
  groupFeedPagination: {
    current: 0,
    pages: 0,
    total: 0,
  },
  createCommunityGroupStatus: {
    status: 'idle',
    error: null,
  },
  getBasicCommunityGroupStatus: {
    status: 'idle',
    error: null,
  },
  getGroupDetailsStatus: {
    status: 'idle',
    error: null,
  },
  getGroupFeedStatus: {
    status: 'idle',
    error: null,
  },
  getCommunityGroupsStatus: {
    status: 'idle',
    error: null,
  },
  getGroupMembersStatus: {
    status: 'idle',
    error: null,
  },
  getCreatedGroupDetailsStatus: {
    status: 'idle',
    error: null,
  },
  removeGroupMembersStatus: {
    status: 'idle',
    error: null,
  },
};

const groupSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    setSelectedGroupId: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    clearSelectedGroupId: (state) => {
      state.selectedId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCommunityGroups.pending, (state) => {
      state.getCommunityGroupsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getCommunityGroups.fulfilled, (state, { payload }) => {
      state.getCommunityGroupsStatus = {
        status: 'success',
        error: null,
      };
      state.groups = payload.model;
      state.groupsPagination = payload.meta;
    });
    builder.addCase(getCommunityGroups.rejected, (state, { payload }) => {
      state.getCommunityGroupsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch member groups',
      };
    });
    builder.addCase(createCommunityGroup.pending, (state) => {
      state.createCommunityGroupStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(createCommunityGroup.fulfilled, (state) => {
      state.createCommunityGroupStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(createCommunityGroup.rejected, (state, { payload }) => {
      state.createCommunityGroupStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to create commuinty group',
      };
    });
    builder.addCase(getBasicCommunityGroups.pending, (state) => {
      state.getBasicCommunityGroupStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getBasicCommunityGroups.fulfilled, (state, { payload }) => {
      state.getBasicCommunityGroupStatus = {
        status: 'success',
        error: null,
      };
      state.basicGroups = payload;
    });
    builder.addCase(getBasicCommunityGroups.rejected, (state, { payload }) => {
      state.getBasicCommunityGroupStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch community groups list',
      };
    });
    builder.addCase(getGroupMembers.pending, (state) => {
      state.getGroupMembersStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getGroupMembers.fulfilled, (state, { payload }) => {
      state.getGroupMembersStatus = {
        status: 'success',
        error: null,
      };
      state.groupMembers = payload.model;
      state.groupMembersPagination = payload.meta;
    });
    builder.addCase(getGroupMembers.rejected, (state, { payload }) => {
      state.getGroupMembersStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch community group members',
      };
    });
    builder.addCase(getGroupDetails.pending, (state) => {
      state.getGroupDetailsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getGroupDetails.fulfilled, (state, { payload }) => {
      state.getGroupDetailsStatus = {
        status: 'success',
        error: null,
      };
      state.group = payload.group;
      state.groupMembers = payload.groupMembers.model;
      state.groupMembersPagination = payload.groupMembers.meta;
      state.groupFeed = payload.groupFeed.model;
      state.groupFeedPagination = payload.groupFeed.meta;
    });
    builder.addCase(getGroupDetails.rejected, (state, { payload }) => {
      state.getGroupDetailsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch community group details',
      };
    });
    builder.addCase(getCreatedGroupDetails.pending, (state) => {
      state.getCreatedGroupDetailsStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(getCreatedGroupDetails.fulfilled, (state, { payload }) => {
      state.getCreatedGroupDetailsStatus = {
        status: 'success',
        error: null,
      };
      state.group = payload.group;
      state.groupMembers = payload.groupMembers.model;
      state.groupMembersPagination = payload.groupMembers.meta;
      state.groupFeed = payload.groupFeed.model;
      state.groupFeedPagination = payload.groupFeed.meta;
      state.selectedId = payload.group.id;
    });
    builder.addCase(getCreatedGroupDetails.rejected, (state, { payload }) => {
      state.getCreatedGroupDetailsStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to fetch community group details',
      };
    });
    builder.addCase(removeGroupMembers.pending, (state) => {
      state.removeGroupMembersStatus = {
        status: 'loading',
        error: null,
      };
    });
    builder.addCase(removeGroupMembers.fulfilled, (state) => {
      state.removeGroupMembersStatus = {
        status: 'success',
        error: null,
      };
    });
    builder.addCase(removeGroupMembers.rejected, (state, { payload }) => {
      state.removeGroupMembersStatus = {
        status: 'failure',
        error: payload?.message
          ? payload.message
          : 'Error trying to remove members from group',
      };
    });
    builder.addCase(resetAll, () => initialState);
  },
});

export const { setSelectedGroupId, clearSelectedGroupId } = groupSlice.actions;

export default groupSlice.reducer;
